import { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, 
  Users, 
  ArrowLeft,
  Crown,
  Settings,
  UserPlus,
  Shield,
  Phone,
  Mail,
  Calendar,
  MoreVertical,
  Edit2,
  Trash2,
  RefreshCw,
  Check,
  X,
  Cloud,
  Key,
  Webhook,
  Copy,
  Eye,
  EyeOff,
  HelpCircle,
  ExternalLink,
  Save,
  RefreshCcw,
  Star,
  UserX,
  UserCheck,
  AlertTriangle,
  Voicemail,
  Server,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

import { API_BASE_URL } from '../utils/api';
import StarCodesEditor from '../components/StarCodesEditor';
const API_URL = API_BASE_URL;

// Plan configurations - wholesale/retail prices match backend DEFAULT_PRICING
const DEFAULT_PLANS = {
  free: { name: 'Free', wholesale: 0, retail: 0, color: 'bg-slate-500' },
  pro: { name: 'Pro', wholesale: 0, retail: 0, color: 'bg-cyan-500' }
};

const ROLES = [
  { value: 'standard', label: 'Standard User' },
  { value: 'phone_admin', label: 'Phone Admin' },
  { value: 'customer_admin', label: 'Customer Admin' }
];

export default function PartnerCustomerManagement() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { partner } = useOutletContext();
  
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Lines state
  const [lines, setLines] = useState([]);
  const [loadingLines, setLoadingLines] = useState(false);
  const [linesFilter, setLinesFilter] = useState('all');
  // Dialog states
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // New user form
  const [newUser, setNewUser] = useState({
    name: '',
    phone_number: '',
    email: '',
    role: 'standard'
  });

  // PBX Settings state
  const [pbxSettings, setPbxSettings] = useState({
    provider: '',
    api_base_url: '',
    api_username: '',
    api_password: '',
    billing_email: '',
    contacts_url: '',
    voicemail_url: ''
  });
  const [showApiPassword, setShowApiPassword] = useState(false);
  const [savingPbx, setSavingPbx] = useState(false);
  
  // Webhook state
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  // Star Codes state
  const [starCodes, setStarCodes] = useState([]);
  const [starCodesLoading, setStarCodesLoading] = useState(false);
  const [starCodesSaving, setStarCodesSaving] = useState(false);
  const [starCodesInitialized, setStarCodesInitialized] = useState(false);
  const [plans, setPlans] = useState(DEFAULT_PLANS);

  useEffect(() => {
    fetchCustomerDetails();
    fetchCustomerUsers();
    fetchWebhookDetails();
    fetchStarCodes();
    fetchPricing();
  }, [customerId]);

  // Fetch lines when Lines tab is selected
  useEffect(() => {
    if (activeTab === 'lines' && lines.length === 0) {
      fetchLines();
    }
  }, [activeTab]);

  const fetchLines = async () => {
    setLoadingLines(true);
    try {
      const res = await axios.get(`${API_URL}/api/partner/customers/${customerId}/lines`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setLines(res.data?.Data || []);
    } catch (error) {
      console.error('Failed to load lines:', error);
    } finally {
      setLoadingLines(false);
    }
  };

  const toggleLineFlag = async (endpoint, serviceName, body) => {
    try {
      await axios.post(`${API_URL}/api/partner/customers/${customerId}/lines/${endpoint}`, body, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      fetchLines();
    } catch (error) {
      console.error(`Failed to toggle ${endpoint}:`, error);
    }
  };

  // Update PBX settings when customer data loads
  useEffect(() => {
    if (customer) {
      setPbxSettings({
        provider: customer.provider || '',
        api_base_url: customer.api_base_url || '',
        api_username: customer.api_credentials?.username || '',
        api_password: '',
        billing_email: customer.billing_email || '',
        contacts_url: customer.contacts_url || '',
        voicemail_url: customer.voicemail_url || ''
      });
    }
  }, [customer]);

  const getToken = () => localStorage.getItem('partner_token');

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/billing/pricing/active`);
      const prices = response.data?.prices || {};
      setPlans({
        free: { name: 'Free', wholesale: prices.free?.wholesale_price_monthly || 0, retail: prices.free?.retail_price_monthly || 0, color: 'bg-slate-500' },
        pro: { name: 'Pro', wholesale: prices.pro?.wholesale_price_monthly || 0, retail: prices.pro?.retail_price_monthly || 0, color: 'bg-cyan-500' }
      });
    } catch (error) {
      console.log('Could not fetch pricing, using defaults');
    }
  };

  const fetchCustomerDetails = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/partner/customers/${customerId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setCustomer(response.data);
      setSelectedPlan(response.data.plan || 'free');
    } catch (error) {
      toast.error('Failed to load customer details');
      navigate('/partner/customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerUsers = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/partner/customers/${customerId}/users`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchWebhookDetails = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/partner/customers/${customerId}/webhook`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setWebhookUrl(response.data.webhook_url || '');
      setWebhookSecret(response.data.webhook_secret || '');
    } catch (error) {
      console.error('Failed to fetch webhook details');
    }
  };

  const fetchStarCodes = async () => {
    setStarCodesLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/partner/customers/${customerId}/star-codes`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setStarCodes(response.data.codes || []);
      setStarCodesInitialized(response.data.initialized !== false);
    } catch (error) {
      console.error('Failed to fetch star codes');
    } finally {
      setStarCodesLoading(false);
    }
  };

  const handleSaveStarCodes = async (codes) => {
    setStarCodesSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/partner/customers/${customerId}/star-codes`,
        { codes },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      await fetchStarCodes();
    } catch (error) {
      throw error;
    } finally {
      setStarCodesSaving(false);
    }
  };

  const handleResetStarCodes = async () => {
    setStarCodesSaving(true);
    try {
      console.log('[StarCodes] Resetting to defaults...');
      const response = await axios.post(
        `${API_URL}/api/partner/customers/${customerId}/star-codes/reset`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      console.log('[StarCodes] Reset response:', response.data);
      const newCodes = response.data.codes || [];
      console.log(`[StarCodes] Setting ${newCodes.length} codes`);
      setStarCodes(newCodes);
    } catch (error) {
      console.error('[StarCodes] Reset failed:', error);
      throw error;
    } finally {
      setStarCodesSaving(false);
    }
  };

  const handleInitializeStarCodes = async () => {
    setStarCodesSaving(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/partner/customers/${customerId}/star-codes/initialize`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setStarCodes(response.data.codes || []);
      setStarCodesInitialized(true);
    } catch (error) {
      throw error;
    } finally {
      setStarCodesSaving(false);
    }
  };

  const handleSavePbxSettings = async () => {
    setSavingPbx(true);
    try {
      await axios.put(
        `${API_URL}/api/partner/customers/${customerId}/pbx-settings`,
        pbxSettings,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('PBX settings saved successfully');
      setPbxSettings(prev => ({ ...prev, api_password: '' }));
      fetchCustomerDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save PBX settings');
    } finally {
      setSavingPbx(false);
    }
  };

  const handleRegenerateSecret = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/partner/customers/${customerId}/webhook/regenerate`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setWebhookSecret(response.data.webhook_secret);
      toast.success('Webhook secret regenerated');
    } catch (error) {
      toast.error('Failed to regenerate secret');
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'url') {
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      } else {
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
      }
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handlePlanChange = async () => {
    setProcessing(true);
    try {
      await axios.put(
        `${API_URL}/api/partner/customers/${customerId}/plan`,
        { plan: selectedPlan },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`Plan changed to ${plans[selectedPlan].name}`);
      setShowPlanDialog(false);
      fetchCustomerDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change plan');
    } finally {
      setProcessing(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(
        `${API_URL}/api/partner/customers/${customerId}/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('User role updated');
      fetchCustomerUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.phone_number) {
      toast.error('Name and phone number are required');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(
        `${API_URL}/api/partner/customers/${customerId}/users`,
        newUser,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('User added successfully');
      setShowAddUserDialog(false);
      setNewUser({ name: '', phone_number: '', email: '', role: 'standard' });
      fetchCustomerUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setProcessing(false);
    }
  };

  const handleDisableUser = async (userId, isActive) => {
    try {
      await axios.put(
        `${API_URL}/api/partner/customers/${customerId}/users/${userId}/status`,
        { is_active: isActive },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(isActive ? 'User enabled' : 'User disabled');
      fetchCustomerUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(
        `${API_URL}/api/partner/customers/${customerId}/users/${userId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('User permanently deleted');
      fetchCustomerUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'customer_admin':
        return <Badge className="bg-amber-500">Admin</Badge>;
      case 'phone_admin':
        return <Badge className="bg-blue-500">Phone Admin</Badge>;
      case 'developer':
        return <Badge className="bg-purple-500">Developer</Badge>;
      default:
        return <Badge variant="secondary">Standard</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="partner-customer-management">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate('/partner/customers')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-foreground">{customer?.name}</h1>
            <Badge className={plans[customer?.plan]?.color || 'bg-slate-500'}>
              {plans[customer?.plan]?.name || 'Free'} Plan
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1">
            Account #{customer?.account_number} • Managing as Partner
          </p>
        </div>
        <Button variant="outline" onClick={() => setShowPlanDialog(true)}>
          <Crown className="w-4 h-4 mr-2" />
          Change Plan
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="lines" data-testid="partner-tab-lines">Lines</TabsTrigger>
          <TabsTrigger value="team">Team Members ({users.length})</TabsTrigger>
          <TabsTrigger value="starcodes">
            <Star className="w-4 h-4 mr-1.5" />
            Star Codes
          </TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Current Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-500" />
                  <span className="text-2xl font-bold">{plans[customer?.plan]?.name || 'Free'}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {plans[customer?.plan]?.wholesale === 0 ? 'Free' : `$${plans[customer?.plan]?.wholesale}/user/month`}
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Team Size</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span className="text-2xl font-bold">{users.length}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Active users</p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Customer Since</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-green-500" />
                  <span className="text-2xl font-bold">{formatDate(customer?.created_at)}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Member since</p>
              </CardContent>
            </Card>
          </div>

          {/* Company Details */}
          <Card className="border-border/50 mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Company Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Company Name</p>
                <p className="font-medium">{customer?.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Account Number</p>
                <p className="font-medium font-mono">{customer?.account_number}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">SIP Domain</p>
                <p className="font-medium">{customer?.sip_domain_id || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge variant={customer?.status === 'active' ? 'default' : 'secondary'}>
                  {customer?.status || 'trial'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lines Tab */}
        <TabsContent value="lines">
          <Card className="border-border/50" data-testid="partner-lines-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="w-5 h-5 text-primary" />
                    All Lines
                  </CardTitle>
                  <CardDescription>
                    View and configure PBX lines for this customer.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-0.5 bg-muted/50 rounded-lg p-1 border border-border/50">
                    {['all', 'queues', 'extensions', 'virtual'].map(f => (
                      <button
                        key={f}
                        onClick={() => setLinesFilter(f)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                          linesFilter === f
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                        data-testid={`partner-lines-filter-${f}`}
                      >
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchLines} data-testid="partner-refresh-lines-btn">
                    <RefreshCw className={`w-4 h-4 ${loadingLines ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingLines ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : lines.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No lines found. Check PBX connection settings.</p>
              ) : (
                <div className="space-y-2">
                  {lines
                    .filter(line => {
                      if (linesFilter === 'all') return true;
                      if (linesFilter === 'queues') return line.IsQueue || line.Type === 'queue';
                      if (linesFilter === 'extensions') return line.Type === 'extension';
                      if (linesFilter === 'virtual') return ['virtual', 'voicemail', 'ddi'].includes(line.Type);
                      return true;
                    })
                    .map(line => {
                      const isQueue = line.IsQueue || line.Type === 'queue';
                      const hasAA = line.HasAutoAttendant;
                      const isAH = line.IsAfterHours;
                      const isMain = line.IsMainLine;
                      const isDDI = line.IsDDI || line.Type === 'ddi';
                      const formatPhone = (p) => {
                        if (!p) return '';
                        if (p.startsWith('64') && p.length > 9) return '0' + p.slice(2);
                        return p;
                      };

                      return (
                        <div
                          key={line.ServiceName}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                            isMain ? 'border-l-4 border-l-emerald-500 border-border/50' :
                            isQueue ? 'border-l-4 border-l-primary border-border/50' : 'border-border/30'
                          }`}
                          data-testid={`partner-line-${line.ServiceName}`}
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="flex-shrink-0">
                              {isQueue ? (
                                <Users className="w-5 h-5 text-primary" />
                              ) : (
                                <Phone className="w-5 h-5 text-muted-foreground" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm truncate">{line.Name || line.DisplayName}</p>
                                {isDDI && <Badge variant="outline" className="text-xs border-amber-500 text-amber-500 flex-shrink-0">DDI</Badge>}
                                {isMain && <Badge variant="outline" className="text-xs border-emerald-500 text-emerald-500 flex-shrink-0">Main</Badge>}
                                {isQueue && <Badge variant="outline" className="text-xs border-primary text-primary flex-shrink-0">Queue</Badge>}
                                {hasAA && <Badge variant="outline" className="text-xs border-cyan-500 text-cyan-500 flex-shrink-0">AA</Badge>}
                                {isAH && <Badge variant="outline" className="text-xs border-amber-400 text-amber-400 flex-shrink-0">AH</Badge>}
                              </div>
                              <p className="text-xs text-muted-foreground font-mono">
                                {line.Extension ? `Ext ${line.Extension} \u2022 ` : ''}{formatPhone(line.ServiceName)}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-4 flex-shrink-0">
                            <button
                              onClick={() => toggleLineFlag('set-queue', line.ServiceName, { service_name: line.ServiceName, is_queue: !isQueue })}
                              className={`flex items-center gap-1 text-xs transition-colors ${
                                isQueue ? 'text-primary' : 'text-muted-foreground hover:text-primary'
                              }`}
                              title={isQueue ? 'Remove from queues' : 'Mark as queue'}
                              data-testid={`partner-line-toggle-queue-${line.ServiceName}`}
                            >
                              {isQueue ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                              <span>Queue</span>
                            </button>
                            <button
                              onClick={() => toggleLineFlag('set-auto-attendant', line.ServiceName, { service_name: line.ServiceName, has_auto_attendant: !hasAA })}
                              className={`flex items-center gap-1 text-xs transition-colors ${
                                hasAA ? 'text-cyan-400' : 'text-muted-foreground hover:text-cyan-400'
                              }`}
                              title={hasAA ? 'Remove auto-attendant' : 'Mark as auto-attendant'}
                              data-testid={`partner-line-toggle-aa-${line.ServiceName}`}
                            >
                              {hasAA ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                              <span>AA</span>
                            </button>
                            <button
                              onClick={() => toggleLineFlag('set-after-hours', line.ServiceName, { service_name: line.ServiceName, is_after_hours: !isAH })}
                              className={`flex items-center gap-1 text-xs transition-colors ${
                                isAH ? 'text-amber-400' : 'text-muted-foreground hover:text-amber-400'
                              }`}
                              title={isAH ? 'Remove after-hours' : 'Mark as after-hours'}
                              data-testid={`partner-line-toggle-ah-${line.ServiceName}`}
                            >
                              {isAH ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                              <span>AH</span>
                            </button>
                            <button
                              onClick={() => toggleLineFlag('set-main-line', line.ServiceName, { service_name: line.ServiceName, is_main_line: !isMain })}
                              className={`flex items-center gap-1 text-xs transition-colors ${
                                isMain ? 'text-emerald-400' : 'text-muted-foreground hover:text-emerald-400'
                              }`}
                              title={isMain ? 'Remove main line (appears in both Queues & After Hours)' : 'Mark as main line (appears in both Queues & After Hours)'}
                              data-testid={`partner-line-toggle-main-${line.ServiceName}`}
                            >
                              {isMain ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                              <span>Main</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  <p className="text-xs text-muted-foreground text-center pt-2">
                    {lines.filter(l => l.IsQueue || l.Type === 'queue').length} queues &bull; {lines.length} total lines
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Members Tab */}
        <TabsContent value="team">
          <Card className="border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Team Members
                  </CardTitle>
                  <CardDescription>Manage users for this customer</CardDescription>
                </div>
                <Button onClick={() => setShowAddUserDialog(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No users found for this customer
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone / Extension</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {user.phone_number}
                          {user.extension && <span className="text-muted-foreground ml-2">x{user.extension}</span>}
                        </TableCell>
                        <TableCell>{user.email || '-'}</TableCell>
                        <TableCell>
                          <Select
                            value={user.role}
                            onValueChange={(value) => handleRoleChange(user.id, value)}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ROLES.map((role) => (
                                <SelectItem key={role.value} value={role.value}>
                                  {role.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.is_active !== false ? 'default' : 'destructive'}>
                            {user.is_active !== false ? 'Active' : 'Disabled'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {user.is_active !== false ? (
                                <DropdownMenuItem 
                                  onClick={() => handleDisableUser(user.id, false)}
                                >
                                  <UserX className="w-4 h-4 mr-2" />
                                  Disable User
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem 
                                  onClick={() => handleDisableUser(user.id, true)}
                                >
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Enable User
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onSelect={(e) => e.preventDefault()}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Permanently
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="flex items-center gap-2">
                                      <AlertTriangle className="w-5 h-5 text-destructive" />
                                      Permanently Delete User
                                    </AlertDialogTitle>
                                    <AlertDialogDescription asChild>
                                      <div className="space-y-3">
                                        <p>
                                          Are you sure you want to permanently delete <strong>{user.name || user.phone_number}</strong>?
                                        </p>
                                        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                                          This will permanently remove this user's account and cannot be undone. Their call history and records will be preserved but they will no longer be able to log in.
                                        </div>
                                      </div>
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteUser(user.id)}
                                      className="bg-destructive hover:bg-destructive/90"
                                    >
                                      Delete Permanently
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Star Codes Tab */}
        <TabsContent value="starcodes">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" />
                Star Codes
              </CardTitle>
              <CardDescription>
                Manage star codes for {customer?.name}. These codes allow users to access PBX features directly from their dialers.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <StarCodesEditor
                codes={starCodes}
                onSave={handleSaveStarCodes}
                onReset={handleResetStarCodes}
                onInitialize={handleInitializeStarCodes}
                loading={starCodesLoading}
                saving={starCodesSaving}
                initialized={starCodesInitialized}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          {/* Cloud PBX Provider Section */}
          <Card className="border-border/50">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Cloud className="w-5 h-5 text-primary" />
                    Cloud PBX Connection
                  </CardTitle>
                  <CardDescription>
                    Provider, API endpoint, and billing contact
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select 
                    value={pbxSettings.provider || 'hero'} 
                    onValueChange={(val) => setPbxSettings({...pbxSettings, provider: val})}
                  >
                    <SelectTrigger data-testid="partner-provider-select">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hero">Hero / Connect-US</SelectItem>
                      <SelectItem value="2talk">2Talk</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Billing Email</Label>
                  <Input
                    type="email"
                    value={pbxSettings.billing_email}
                    onChange={e => setPbxSettings({ ...pbxSettings, billing_email: e.target.value })}
                    placeholder="billing@customer.com"
                    data-testid="partner-billing-email"
                  />
                  <p className="text-xs text-muted-foreground">For price change notifications</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>API Base URL</Label>
                <Input
                  value={pbxSettings.api_base_url}
                  onChange={e => setPbxSettings({ ...pbxSettings, api_base_url: e.target.value })}
                  placeholder="https://admin1.connect-us.co.nz"
                  data-testid="partner-api-base-url"
                />
              </div>
              <div className="space-y-2">
                <Label>Contacts Sync URL</Label>
                <Input
                  value={pbxSettings.contacts_url}
                  onChange={e => setPbxSettings({ ...pbxSettings, contacts_url: e.target.value })}
                  placeholder="https://portal1.connect-us.co.nz/api/contacts.php?l=..."
                  data-testid="partner-contacts-url"
                />
                <p className="text-xs text-muted-foreground">External URL to sync phonebook contacts</p>
              </div>
            </CardContent>
          </Card>

          {/* Voicemail Server Section */}
          <Card className="border-border/50" data-testid="partner-voicemail-config-card">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Voicemail className="w-5 h-5 text-primary" />
                    Voicemail Server
                  </CardTitle>
                  <CardDescription>
                    Configure the voicemail server URL for accessing user voicemails
                  </CardDescription>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <HelpCircle className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      <p>This is the URL of the Horde webmail server where voicemails are stored. Users authenticate with their SIP credentials.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Voicemail Server URL</Label>
                <Input
                  value={pbxSettings.voicemail_url}
                  onChange={e => setPbxSettings({ ...pbxSettings, voicemail_url: e.target.value })}
                  placeholder="https://line1.connect-us.co.nz"
                  data-testid="partner-voicemail-url"
                />
                <p className="text-xs text-muted-foreground">
                  The Horde webmail server URL where voicemails are stored
                </p>
              </div>
            </CardContent>
          </Card>

          {/* API Credentials Section */}
          <Card className="border-border/50">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-primary" />
                    API Credentials
                  </CardTitle>
                  <CardDescription>
                    Required for Click-to-Call, CDR sync, and contact management
                  </CardDescription>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <HelpCircle className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      <p>Find these credentials in the customer&apos;s PBX portal under API Settings.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>API Username</Label>
                  <Input
                    value={pbxSettings.api_username}
                    onChange={e => setPbxSettings({ ...pbxSettings, api_username: e.target.value })}
                    placeholder="email@customer.com"
                    data-testid="partner-api-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Password</Label>
                  <div className="relative">
                    <Input
                      type={showApiPassword ? 'text' : 'password'}
                      value={pbxSettings.api_password}
                      onChange={e => setPbxSettings({ ...pbxSettings, api_password: e.target.value })}
                      placeholder={customer?.api_credentials?.has_password ? '••••••••' : 'API password'}
                      className="pr-10"
                      data-testid="partner-api-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiPassword(!showApiPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showApiPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {customer?.api_credentials?.has_password && (
                    <p className="text-xs text-emerald-500">Password configured</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Webhook Configuration Section */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Webhook className="w-5 h-5 text-primary" />
                Webhook Configuration
              </CardTitle>
              <CardDescription>
                Receive real-time call events from the PBX
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Setup Instructions */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex gap-3">
                  <HelpCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Setup Instructions</p>
                    <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
                      <li>Log into the customer&apos;s Connect-US portal</li>
                      <li>Navigate to API Settings → Webhooks</li>
                      <li>Create a new webhook using the URL and Secret below</li>
                      <li>Select events: Call Ringing, Call Answered, Call Ended</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Webhook URL */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Webhook URL</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-secondary/50 rounded text-sm font-mono truncate border border-border/50">
                    {webhookUrl || "Loading..."}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => copyToClipboard(webhookUrl, 'url')}
                    disabled={!webhookUrl}
                  >
                    {copiedUrl ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Secret Token */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  Secret Token
                  <Badge variant="outline" className="text-xs">For verification</Badge>
                </Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-secondary/50 rounded text-sm font-mono border border-border/50">
                    {webhookSecret || "Loading..."}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => copyToClipboard(webhookSecret, 'secret')}
                    disabled={!webhookSecret}
                    title="Copy secret"
                  >
                    {copiedSecret ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleRegenerateSecret}
                    title="Regenerate secret"
                  >
                    <RefreshCcw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSavePbxSettings} disabled={savingPbx} data-testid="save-pbx-settings-btn">
              {savingPbx ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save PBX Settings
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Change Plan Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Customer Plan</DialogTitle>
            <DialogDescription>
              Select a new plan for {customer?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {Object.entries(plans).map(([planId, plan]) => (
              <div
                key={planId}
                className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                  selectedPlan === planId 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setSelectedPlan(planId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${plan.color}`} />
                    <span className="font-medium">{plan.name}</span>
                  </div>
                  <div className="text-right">
                    {plan.wholesale === 0 ? (
                      <span className="font-bold">Free</span>
                    ) : (
                      <>
                        <span className="font-bold">${plan.wholesale}/user</span>
                        <span className="text-xs text-muted-foreground ml-2">(RRP: ${plan.retail})</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handlePlanChange} disabled={processing}>
              {processing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Add a new team member to {customer?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="John Smith"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Phone Number (SIP) *</Label>
              <Input
                placeholder="830200010001"
                value={newUser.phone_number}
                onChange={(e) => setNewUser({ ...newUser, phone_number: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="john@company.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUserDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddUser} disabled={processing}>
              {processing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Add User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
